import { type Span, trace, type Tracer } from '@opentelemetry/api'
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-http'
import { registerInstrumentations } from '@opentelemetry/instrumentation'
import { DocumentLoadInstrumentation } from '@opentelemetry/instrumentation-document-load'
import { FetchInstrumentation } from '@opentelemetry/instrumentation-fetch'
import { resourceFromAttributes } from '@opentelemetry/resources'
import {
  BatchSpanProcessor,
  ParentBasedSampler,
  type SpanProcessor,
  TraceIdRatioBasedSampler,
} from '@opentelemetry/sdk-trace-base'
import { WebTracerProvider } from '@opentelemetry/sdk-trace-web'

import { getTelemetryUserId } from './telemetry-user'

// initBrowserTracing이 등록한 provider를 보관해, 다른 모듈이 동일 provider의 tracer를
// 직접 쓸 수 있게 한다. global tracer(getTracer)는 Sentry 등 다른 provider가 선점할 수
// 있어 우리 WebTracerProvider(Tempo로 export)를 못 쓰는 경우가 있다.
let webTracerProvider: WebTracerProvider | undefined

/**
 * 모든 span에 현재 유저(user.id)를 부착한다. Sentry.setUser의 대체로,
 * 백엔드 MDCFilter의 user.id span attribute와 같은 key를 써 Tempo에서 유저별 추적이 된다.
 */
class UserSpanProcessor implements SpanProcessor {
  onStart(span: Span): void {
    const userId = getTelemetryUserId()
    if (userId !== undefined) {
      span.setAttribute('user.id', userId)
    }
  }

  onEnd(): void {}

  forceFlush(): Promise<void> {
    return Promise.resolve()
  }

  shutdown(): Promise<void> {
    return Promise.resolve()
  }
}

interface BrowserTracingOptions {
  serviceName: string
  environment: string
  /** OTLP/HTTP traces 엔드포인트. same-origin 프록시 경로를 사용한다. */
  otlpUrl: string
  /** 0~1 사이의 트레이스 샘플링 비율. */
  sampleRatio: number
  /**
   * 트레이싱에서 제외할 URL 패턴. 텔레메트리 전송 요청(/api/telemetry) 자체가
   * 다시 span을 만들어 무한 증식하는 것을 막고, 노이즈성 요청을 걸러낸다.
   */
  ignoreUrls: (string | RegExp)[]
  /**
   * traceparent 헤더를 주입할 cross-origin URL 패턴. same-origin은 기본 주입되지만,
   * 브라우저가 직접 호출하는 cross-origin 백엔드(예: api.podospeaking.com)는 명시해야
   * traceparent가 전파돼 백엔드 span과 trace_id가 연결된다.
   */
  propagateTraceHeaderCorsUrls?: (string | RegExp)[]
}

/**
 * 브라우저용 OpenTelemetry WebTracerProvider를 초기화한다.
 * document-load(페이지 로드)와 fetch(API 호출) span을 자동 수집해
 * same-origin 프록시(`/api/telemetry/v1/traces`)를 거쳐 Tempo로 보낸다.
 */
export function initBrowserTracing({
  serviceName,
  environment,
  otlpUrl,
  sampleRatio,
  ignoreUrls,
  propagateTraceHeaderCorsUrls,
}: BrowserTracingOptions): void {
  const provider = new WebTracerProvider({
    resource: resourceFromAttributes({
      'service.name': serviceName,
      'deployment.environment': environment,
    }),
    sampler: new ParentBasedSampler({ root: new TraceIdRatioBasedSampler(sampleRatio) }),
    spanProcessors: [new UserSpanProcessor(), new BatchSpanProcessor(new OTLPTraceExporter({ url: otlpUrl }))],
  })

  // ZoneContextManager(zone.js)는 Promise rejection을 가로채 window.unhandledrejection
  // 이벤트를 막으므로 사용하지 않는다. 기본 컨텍스트 매니저로 등록한다.
  provider.register()

  registerInstrumentations({
    tracerProvider: provider,
    instrumentations: [
      new DocumentLoadInstrumentation(),
      new FetchInstrumentation({ ignoreUrls, propagateTraceHeaderCorsUrls }),
    ],
  })

  webTracerProvider = provider
}

/**
 * initBrowserTracing이 등록한 WebTracerProvider의 tracer를 반환한다.
 * 아직 초기화되지 않았으면 global tracer로 폴백한다.
 */
export function getWebTracer(): Tracer {
  return (webTracerProvider ?? trace).getTracer('web')
}
